<template>
  <div class="CustomerFiles">
    <v-row class="header d-flex">
      <v-col class="col-12 col-sm-8 text-sm-left text-center">
        <div class="title">{{ $t('crm.details-attachement.title') }}</div>
        <div class="subtitle">{{ $t('crm.details-attachement.subtitle') }}</div>
      </v-col>

      <v-col class="col-12 col-sm-4 text-sm-right text-center">
        <v-btn
          v-if="buttonsAreVisible"
          text
          class="theme-btn app-button-primary color"
          @click="openUploadDialog(null)"
        >
          {{ $t('crm.details-attachement.add_document') }}
        </v-btn>
      </v-col>
    </v-row>
    <v-divider class="header-divider mt-8" />
    <v-row class="content mb-8">
      <v-data-table
        height="150%"
        :headers="headers"
        :items="items"
        item-key="document_kind"
        class="attachments-table"
        :no-data-text="$t('common.no-data')"
        hide-default-footer
        disable-pagination
      >
        <template v-slot:[`item.document_kind`]="{ item }">
          <SelectInput
            v-model="item.document_kind"
            :default="item.document_kind"
            :options="documents"
            class="documents-select mt-2 my-1"
            item-value="code"
            item-text="label"
            @input="updateDocument(item.id, 'document_kind', item.document_kind)"
          />
        </template>
        <template v-slot:[`item.expiration_date`]="{ item }">
          <v-col cols="9">
            <DatePicker
              v-model="item.expiration_date"
              :datePickedProp="item.expiration_date"
              labelColor="purple-dark"
              class="date-picker mt-2 my-1"
              @input="updateDocument(item.id, 'expiration_date', $event)"
            />
          </v-col>
        </template>
        <template v-slot:[`item.control`]="{ item }">
          <div
            v-if="buttonsAreVisible"
            class="d-flex align-center"
          >
            <v-btn
              :disabled="item.control"
              text
              class="theme-btn validate-btn"
              @click="validateData(item.document_kind, true)"
            >{{ $t('crm.details-attachement.validate') }}
            </v-btn>
            <v-btn
              :disabled="item.control === false"
              text
              class="theme-btn error-btn ml-md-2 ml-0"
              @click="validateData(item.document_kind, false)"
            >{{ $t('crm.details-attachement.refuse') }}
            </v-btn>
            <!-- <v-btn
              v-if="item.control === false"
              text
              class="theme-btn error-btn ml-md-2 ml-0"
            >{{ $t('crm.details-attachement.delete') }}
            </v-btn> -->
          </div>
          <div v-else>
            {{ $t(`main.enums.status.${customerFileStatus}`) }}
          </div>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-space-between mt-2 my-1">
            <!-- <v-btn
              text
              class="theme-btn app-button-secondary color-white"
            >{{ $t('crm.details-attachement.preview') }}
            </v-btn> -->
            <v-spacer />
            <v-btn
              text
              class="theme-btn app-button-secondary color-white ml-2"
              @click="openUploadDialog(item.id)"
            >
              {{ $t('common.action.display') }}
            </v-btn>
          </div>
        </template>
      </v-data-table>
      <div class="custom-footer pa-4 d-flex">
        <div class="flex-grow-1 text-left">
          <span class="footer-length"> {{ currentCustomer.supporting_documents.length }}</span>
          <span> {{ $t('crm.details-attachement.data') }}</span>
        </div>
        <div class="d-flex justify-center">
          <v-btn
            v-if="sendCustomerFileButtonIsVisible"
            block
            text
            class="theme-btn app-button-primary color nice-padding"
            :disabled="!isReadyToValidate"
            @click="sendCustomerFile"
          >{{ $t('common.action.send-customer-file') }}</v-btn>
        </div>
      </div>
    </v-row>
    <UploadFilesModal
      v-if="selectedDoc"
      :isOpened="dialog"
      :personId="currentCustomer.id"
      :documentId="selectedDoc.id"
      :documentFiles="selectedDoc.pages"
      :documentKind.sync="selectedDoc.document_kind"
      :documentStatus="selectedDoc.status || ''"
      @close="dialog = false"
      @updated="onDocumentUpdated"
    />
  </div>
</template>

<script>
  import DatePicker from '@/components/ui/inputs/DatePicker'
  import SelectInput from '@/components/ui/inputs/SelectInput'
  import CommonMixin from '@/mixins/store/CommonMixin'
  import AdminMixin from '@/mixins/store/AdminMixin'
  import ApiService from '@/services/ApiService'
  import UploadFilesModal from '@/components/ui/common/UploadFilesModal/UploadFilesModal'
  import RequestAPI from '@/services/RequestAPI'
  import { PERSON_STATUS } from '@/services/models.const'

  export default {
    components: {
      UploadFilesModal,
      DatePicker,
      SelectInput,
    },
    mixins: [CommonMixin, AdminMixin,],
    data() {
      return {
        dialog: false,
        selectedDoc: null,
        items: [],
        headers: [
          {
            text: `${this.$t('crm.details-attachement.type')}`,
            value: 'document_kind',
            width: '40%',
          },
          { text: `${this.$t('crm.details-attachement.expiry_date')}`, value: 'expiration_date', width: '25%', },
          { text: `${this.$t('crm.details-attachement.status')}`, value: 'control', width: '15%', },
          { text: '', value: 'actions', sortable: false, width: '20%', },
        ],
        documents: [
          {
            code: 'id_card',
            label: this.$t('main.enums.documents.id_card'),
          },
          {
            code: 'proof_of_address',
            label: this.$t('main.enums.documents.proof_of_address'),
          },
          {
            code: 'bank_account_identity',
            label: this.$t('main.enums.documents.bank_account_identity'),
          },
          {
            code: 'tax_self_certification',
            label: this.$t('main.enums.documents.tax_self_certification'),
          },
          {
            code: 'proof_of_tax_address',
            label: this.$t('main.enums.documents.proof_of_tax_address'),
          },
          {
            code: 'guardianship_document',
            label: this.$t('main.enums.documents.guardianship_document'),
          },
        ],
      }
    },
    computed: {
      buttonsAreVisible() {
        return this.currentCustomerFile.status !== PERSON_STATUS.completed
      },
      sendCustomerFileButtonIsVisible() {
        return this.currentCustomerFile.status === PERSON_STATUS.validated_by_user
      },
    },
    mounted() {
      this.loadData()
    },
    methods: {
      async sendCustomerFile() {
        this.toggleAppLoader(true)
        await this.refreshCustomerFields()

        if (this.currentCustomerFile) {
          const result = await RequestAPI.postAdvisorFinalValidation(this.currentCustomerFile.id)
          if (result.errors) {
            this.$message({
              type: 'error',
              text: 'send_file_customer_error',
            })
          } else {
            this.$message({
              type: 'success',
              text: 'send_file_customer_success',
            })
            await this.refreshCustomerFields()
          }
          this.toggleAppLoader(false)
        } else if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('No customer file')
        }
        this.toggleAppLoader(false)
      },
      async onDocumentUpdated() {
        await this.refreshCustomerFields()
        await this.loadData()
        this.dialog = false
      },
      loadData() {
        try {
          this.items = this.extendWithControls(
            JSON.parse(JSON.stringify(this.currentCustomer.supporting_documents)),
            JSON.parse(JSON.stringify(this.currentControls.checks))
          )
        } catch (error) {
          Promise.resolve(error)
        }
      },
      extendWithControls(docs, controls) {
        return docs.map((item, index) => {
          return Object.assign(
            {
              control: controls[item.document_kind],
            },
            item
          )
        })
      },
      async validateData(field, valid) {
        this.toggleAppLoader(true)
        const payload = {
          type: 'person_controls',
          attributes: {},
        }
        payload.attributes[field] = valid
        const result = await ApiService.updateAdvisorCheck(this.personControlId, payload)
        this.$store.commit('admin/setCurrentControls', result)
        await this.onDocumentUpdated()
        this.toggleAppLoader(false)
      },
      openUploadDialog(id) {
        this.dialog = true
        const found = this.currentCustomer.supporting_documents.find((item) => item.id === id)

        this.selectedDoc = found || {
          pages: [],
          document_kind: '',
        }
      },
      async updateDocument(id, field, value) {
        this.toggleAppLoader(true)
        const payload = {
          type: 'supporting_documents',
          id,
          attributes: {},
        }
        payload.attributes[field] = value
        const result = await ApiService.updateDocumentAttribute(id, payload)
        if (result.errors) {
          this.$message({
            type: 'error',
            text: 'update_infos_error',
          })
        } else {
          this.$message({
            type: 'success',
            text: 'update_infos_success',
          })
          this.onDocumentUpdated()
        }
        this.toggleAppLoader(false)
      },
    },
  }
</script>

<style lang="scss">
.CustomerFiles {
  height: 100%;
  .header {
    .title {
      color: $color-001c4b;
      font-size: 1.25em;
      font-weight: 500;
      line-height: 2em;
    }
    .subtitle {
      color: $color-4d4d4f;
      font-size: 1em;
      letter-spacing: 0.24px;
      line-height: 1.5em;
    }
  }
  .header-divider {
    background: rgba(158, 159, 162, 0.32);
    margin-bottom: 47px;
  }
  .content {
    height: 35rem;
    padding-left: 10px;
    .attachments-table {
      width: 100%;
      .v-data-table__wrapper
      > table
      > tbody
      > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
        background: $color-transparent;
      }
      .v-data-table__mobile-row {
        display: contents;
        .documents-select {
          width: 12em;
        }
      }
      tbody > tr:nth-of-type(even) {
        background-color: $color-fafbff;
      }
      td,
      th {
        border-bottom: 1px solid $color-217_223_255 !important;
      }
      th {
        color: $color-009ee0 !important;
        text-transform: uppercase;
        font-weight: 500;
      }
      .validate-btn {
        background-color: $color-00d367;
        color: $color-ffffff !important;
        &.v-btn--disabled {
          background-color: $color-e8e8e8 !important;
        }
      }
      .error-btn {
        background-color: $color-ff524f;
        color: $color-ffffff !important;
      }
      .documents-select {
        width: 20em;

        .v-text-field__details {
          display: none;
        }
      }
      .date-picker {
        .v-text-field__details {
          display: none;
        }
      }
    }
  }
  .custom-footer {
    position: absolute;
    bottom: 12rem;
    width: 92%;
    border-bottom: 1px solid $color-217_223_255;
    text-transform: uppercase;
    font-size: 0.7em;
    .footer-length {
      color: $color-1976d2;
    }
  }
}
</style>
